import { Component, ErrorInfo, ReactNode } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface Props extends WithTranslation {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    window.location.reload();
  };

  render() {
    const t = this.props.t || ((key: string) => key);

    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background to-muted">
          <Card className="w-full max-w-2xl glass-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-destructive/10">
                  <AlertTriangle className="w-6 h-6 text-destructive" />
                </div>
                <div>
                  <CardTitle className="text-2xl">
                    {t("errorBoundary.title")}
                  </CardTitle>
                  <CardDescription>
                    {t("errorBoundary.description")}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm font-mono text-destructive">
                  {this.state.error?.toString()}
                </p>
              </div>

              <div className="pt-2 space-y-2">
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1 pl-2">
                  <li>{t("errorBoundary.tips.refresh")}</li>
                  <li>{t("errorBoundary.tips.clearCache")}</li>
                  <li>{t("errorBoundary.tips.tryLater")}</li>
                  <li>{t("errorBoundary.tips.contact")}</li>
                </ul>
              </div>
            </CardContent>
            <CardFooter className="flex gap-3">
              <Button
                onClick={this.handleReset}
                className="flex items-center gap-2"
              >
                <RefreshCcw className="w-4 h-4" />
                {t("errorBoundary.reload")}
              </Button>
              <Button
                variant="outline"
                onClick={() => (window.location.href = "/")}
              >
                {t("errorBoundary.goHome")}
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default withTranslation("common")(ErrorBoundary);
